import { memo } from "react";
import * as Styled from "./style";
import { NextLink } from "~/components/blocks/common/next-link";
import type { Href } from "~/const/hrefs";
import { PATHS } from "~/const/paths";
import type { G_GetVideoQuery } from "~/lib/graphql";

type ButtonLink = {
  buttonName: string;
  href?: Href;
};

type BreadcrumbsListProps = {
  videoData: G_GetVideoQuery["video"];
};

/**
 * 作品末端の一番下に表示するパンくずリスト
 * @param props {@link BreadcrumbsListProps}
 */
export const BreadcrumbsListBlock = memo(({ videoData }: BreadcrumbsListProps) => {
  const animeCategory = videoData.categories.find((item) => item.name === "アニメ");
  const gravureCategory = videoData.categories.find((item) => item.name === "グラビア");

  const rootLink: ButtonLink = gravureCategory
    ? { buttonName: "グラビア トップ", href: PATHS.VOD_GRAVURE }
    : { buttonName: "DMM TV トップ", href: PATHS.VOD };

  const categoryLink: ButtonLink | undefined = (() => {
    if (gravureCategory) {
      return {
        buttonName: gravureCategory.name,
        href: { pathname: PATHS.VOD_GRAVURE_LIST },
      };
    }
    if (animeCategory) {
      return {
        buttonName: animeCategory.name,
        href: {
          pathname: PATHS.VOD_LIST,
          query: { categories: animeCategory.id },
        },
      };
    }
    if (videoData.categories.length > 0) {
      return {
        buttonName: videoData.categories[0].name,
        href: {
          pathname: PATHS.VOD_LIST,
          query: { categories: videoData.categories[0].id },
        },
      };
    }
    return;
  })();

  const breadCrumbLinks = [
    rootLink,
    categoryLink,
    {
      buttonName:
        videoData.titleName === videoData.seasonName
          ? `${videoData.titleName}`
          : `${videoData.titleName} ${videoData.seasonName}`,
    },
  ];

  /** `undefined`を除去した配列を生成する */
  const notUndefinedBreadCrumbLinks: ButtonLink[] = breadCrumbLinks.flatMap((item) => {
    return item === undefined ? [] : [item];
  });
  return (
    <Styled.DivBreadcrumbsPadding>
      <Styled.Nav aria-labelledby="パンくずリスト">
        {notUndefinedBreadCrumbLinks.map(({ buttonName, href }, idx, array) =>
          idx !== array.length - 1 && href ? (
            <Styled.DivTitleItem isBefore={idx !== 0} key={`breadcrumbs:${idx}`}>
              <NextLink href={href} aria-label={buttonName} aria-disabled={idx === array.length - 1}>
                {buttonName}
              </NextLink>
            </Styled.DivTitleItem>
          ) : (
            <Styled.DivTitleItem isBefore={idx !== 0} key={`breadcrumbs:${idx}`}>
              {buttonName}
            </Styled.DivTitleItem>
          )
        )}
      </Styled.Nav>
    </Styled.DivBreadcrumbsPadding>
  );
});

BreadcrumbsListBlock.displayName = "BreadcrumbsListBlock";
